import React, { useContext, useMemo } from "react";
import { Card, Typography, Tag, Button, Space } from "antd";
import {
  ArrowLeftOutlined,
  CalendarOutlined,
  LinkOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { ThemeContext } from "../../context/ThemeContext";
import { sampleNewsData } from "../Admin/sampleNewsData";
import "./ModernNewsDetails.css";

const { Title, Text, Paragraph } = Typography;
const placeholderImg =
  "https://images.unsplash.com/photo-1506744038136-46273834b3fb?auto=format&fit=crop&w=600&q=80";

const ModernNewsDetails = ({ news, onBack, onSelectNews }) => {
  const { isDark } = useContext(ThemeContext);
  // Merge all news from all sources
  const allNews = useMemo(() => {
    let merged = [];
    Object.keys(sampleNewsData).forEach((source) => {
      merged = merged.concat(
        (sampleNewsData[source] || []).map((item) => ({ ...item, source }))
      );
    });
    return merged;
  }, []);

  if (!news) return null;

  // Related news: same company, not the current news
  const related = allNews.filter(
    (item) => item.company === news.company && item._id !== news._id
  );

  return (
    <div className="modern-news-details">
      <Button
        type="link"
        icon={<ArrowLeftOutlined />}
        onClick={onBack}
        className="news-back-btn"
      >
        Back to News
      </Button>
      <Card className="news-details-card">
        <div className="news-details-header">
          <Tag color="blue">{news.source}</Tag>
          <Text type="secondary" className="news-details-date">
            <CalendarOutlined /> {news.publishedDate}
          </Text>
        </div>
        {news.imageUrl && (
          <div className="news-details-image-container">
            <img
              src={news.imageUrl}
              alt={news.title}
              className="news-details-image"
              onError={(e) => {
                e.target.src = placeholderImg;
              }}
            />
          </div>
        )}
        <Title level={2} className="news-details-title">
          {news.title}
        </Title>
        <Paragraph className="news-details-company">
          <Text strong>{news.company}</Text>
        </Paragraph>
        <Paragraph className="news-details-company">
          <Text strong>{news.subtitle}</Text>
        </Paragraph>
        <Paragraph className="news-details-content">
          {news.description}
        </Paragraph>
        <div className="news-details-footer">
          <Space>
            <Button
              type="primary"
              icon={<LinkOutlined />}
              href={news.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              Read Full Article
            </Button>
          </Space>
        </div>
      </Card>
      {related.length > 0 && (
        <div className="related-news-section">
          <Title level={4} className="related-news-title">
            Related News
          </Title>
          <div className="related-news-row">
            {related.map((item) => (
              <Card
                key={item._id}
                className="related-news-card"
                hoverable
                onClick={() => onSelectNews(item)}
                cover={
                  <img
                    alt="related news"
                    src={placeholderImg}
                    className="related-news-img"
                    loading="lazy"
                  />
                }
              >
                <div className="news-card-header">
                  <Tag color="blue">{item.source}</Tag>
                  <Text type="secondary" className="news-card-date">
                    <CalendarOutlined /> {item.publishedDate}
                  </Text>
                </div>
                <Title level={5} className="news-card-title">
                  {item.title}
                </Title>
                <Paragraph className="news-card-excerpt">
                  {item.content.slice(0, 70)}...
                </Paragraph>
                <div className="news-card-footer">
                  <Text strong>{item.company}</Text>
                  <Button type="link" icon={<EyeOutlined />}>
                    Read
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ModernNewsDetails;
